import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Advice,
    Button,
    CheckboxGroup,
    Input,
    Link,
    Spacer,
    Text,
    useTheme,
} from '@Sage/hoi-poi-ui';
import AbsoluteLoader from 'components/AbsoluteLoader';
import useLoginLiterals from 'utils/literals/useLoginLiterals';
import { ensureRoute } from 'utils/routes';

import { createUser } from 'services/Onboarding';
import SageCopilotLogo from 'components/SageCopilotLogo';

import './styles.scss';

const getParams = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const { token, email } = Object.fromEntries(urlSearchParams.entries());
    return { token, email };
};

const SageCopilotInvite = memo(() => {
    const theme = useTheme();
    const isValidatingForm = useRef(false);
    const touchedRef = useRef({});

    const [state, setState] = useState({
        error: null,
        loading: false,
        initialized: false,
    });

    const [formState, setFormState] = useState({
        email: '',
        fullName: '',
        checks: {
            privacyPolicy: false,
            termsAndConditions: false,
        },
    });

    const [errors, setErrors] = useState({});

    const { loading: literalsLoading, getLiteral } = useLoginLiterals();
    const t = getLiteral;

    useEffect(() => {
        const { token, email } = getParams();
        if (!token || !email) {
            ensureRoute('/sage-copilot-error');
            return;
        }
        setFormState((prev) => ({ ...prev, email }));
        setState((prev) => ({ ...prev, initialized: true }));
    }, []);

    const isRequired = useCallback((value) => (!value ? t('helptext_required_field') : false), [t]);

    const validateField = useCallback(
        (field, value) => {
            if (field === 'checks' && !isValidatingForm.current) return;
            if (isValidatingForm.current) {
                touchedRef.current = { ...touchedRef.current, [field]: true };
            }

            let error = null;

            switch (field) {
                case 'fullName':
                    error = isRequired(value);
                    break;
                case 'checks':
                    if (!value.privacyPolicy || !value.termsAndConditions) {
                        error = {
                            privacyPolicy: !value.privacyPolicy
                                ? t('helptext_required_field')
                                : false,
                            termsAndConditions: !value.termsAndConditions
                                ? t('helptext_required_field')
                                : false,
                        };
                    } else {
                        error = false;
                    }
                    break;
                default:
                    break;
            }

            setErrors({ ...errors, [field]: error });
            return error;
        },
        [errors, isRequired, t],
    );

    const validateForm = useCallback(() => {
        const errors = Object.entries(formState).reduce((obj, [field, value]) => {
            return { ...obj, [field]: validateField(field, value) };
        }, {});

        setErrors(errors);
        return Object.values(errors).filter(Boolean).length === 0;
    }, [formState, validateField]);

    const validForm = useMemo(() => {
        return Object.values(errors).filter(Boolean).length === 0;
    }, [errors]);

    const onBlur = useCallback((field) => {
        touchedRef.current = { ...touchedRef.current, [field]: true };
    }, []);

    const onChange = useCallback(
        (field) => (value) => {
            setFormState({ ...formState, [field]: value });
            validateField(field, value);
        },
        [formState, validateField],
    );

    const handleLink = useCallback((e, url, target = '_blank') => {
        e.stopPropagation();
        window.open(url, target);
    }, []);

    const handleSend = useCallback(
        (e) => {
            const { token } = getParams();
            e?.preventDefault?.();

            isValidatingForm.current = true;

            if (!validateForm()) {
                return;
            }

            setState({
                error: null,
                loading: true,
                initialized: true,
            });

            createUser(
                {
                    name: formState.fullName,
                    privatePolicy: formState.checks.privacyPolicy,
                    termsAndConditions: formState.checks.termsAndConditions,
                },
                token,
            )
                .then(() => {
                    ensureRoute('/sage-copilot-success');
                })
                .catch((e) => {
                    setState({
                        error: t('error_generalerror'),
                        loading: false,
                        initialized: true,
                    });
                    console.error(e);
                    isValidatingForm.current = false;
                });
        },
        [
            formState.checks.privacyPolicy,
            formState.checks.termsAndConditions,
            formState.fullName,
            t,
            validateForm,
        ],
    );

    const getLabelWithLink = useCallback(
        ({ label, link, url, target }) => {
            const labelText = t(label);
            const linkText = t(link);
            const linkUrl = t(url);

            const parts = labelText.split('%@');
            if (parts.length === 1) return labelText;

            return (
                <>
                    {parts[0]}
                    <Link
                        className="sage-copilot-invite__link"
                        onClick={(e) => handleLink(e, linkUrl, target)}
                        variation="primary"
                        bold
                    >
                        {linkText}
                    </Link>
                    {parts[1]}
                </>
            );
        },
        [handleLink, t],
    );

    const getChecksOptions = useMemo(() => {
        return [
            {
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {getLabelWithLink({
                            label: 'label_privacy_policy',
                            link: 'label_privacy_policy_link',
                            url: 'label_privacy_policy_url',
                        })}
                    </Text>
                ),
                value: 'privacyPolicy',
            },
            {
                label: (
                    <Text type="body" color={theme.colors.textLight.primary}>
                        {getLabelWithLink({
                            label: 'label_terms_and_conditions',
                            link: 'label_terms_and_conditions_link',
                            url: 'label_terms_and_conditions_url',
                        })}
                    </Text>
                ),
                value: 'termsAndConditions',
            },
        ];
    }, [getLabelWithLink, theme.colors.textLight.primary]);

    const btnActive = useMemo(() => {
        return (
            formState.email && formState.checks.privacyPolicy && formState.checks.termsAndConditions
        );
    }, [formState.email, formState.checks.privacyPolicy, formState.checks.termsAndConditions]);

    if (literalsLoading || !state.initialized) {
        return (
            <div className="sage-copilot-invite">
                <AbsoluteLoader size="massive" />
            </div>
        );
    }

    return (
        <div className="sage-copilot-invite">
            <form className="sage-copilot-invite__form" onSubmit={handleSend} autoComplete="off">
                <div className="sage-copilot-invite__logo">
                    <SageCopilotLogo />
                </div>
                <Spacer y={3} />
                <Text type="h4" isBold className="sage-copilot-invite__title">
                    {t('label_welcome_sage_copilot')}
                </Text>
                <Spacer y={2} />
                <Text type="body" color={theme.colors.textLight.secondary}>
                    {t('label_accept_invitation_description')}
                </Text>
                <Spacer y={4} />
                {state.error && (
                    <>
                        <Advice type="error" showIcon defaultCollapsed={false} showCollapse={false}>
                            {state.error}
                        </Advice>
                        <Spacer y={2} />
                    </>
                )}
                <Input
                    label={t('label_login_email')}
                    value={formState.email}
                    isReadOnly
                    isFullWidth
                />
                <Input
                    label={t('label_full_name')}
                    placeholder={t('placeholder_name')}
                    isRequired
                    value={formState.fullName}
                    error={touchedRef.current.fullName && errors.fullName}
                    onChange={onChange('fullName')}
                    onBlur={() => onBlur('fullName')}
                    isFullWidth
                    role="presentation"
                    autocomplete="off"
                />
                <Spacer y={8} />
                <CheckboxGroup
                    options={getChecksOptions}
                    onChange={onChange('checks')}
                    value={formState.checks}
                    isFullWidth
                    error={errors.checks}
                    color="actionMajor"
                />
                <Spacer y={2} />
                <div className="sage-copilot-invite__actions">
                    <Button
                        type="primary"
                        size="big"
                        isLoading={state.loading}
                        isDisabled={(!validForm && isValidatingForm.current) || !btnActive}
                        className="sage-copilot-invite__submit-button"
                        onClick={handleSend}
                    >
                        {t('label_join_now')}
                    </Button>
                </div>
            </form>
        </div>
    );
});

export default SageCopilotInvite;
